import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { Loader2, CheckCircle2, Plus, BookMarked, Pencil, Mail, Phone, Zap, Network, Building2, TrendingUp, TrendingDown } from "lucide-react";

export const INFLUENCE_META = {
  decision_maker: { label: "Decision maker", cls: "bg-red-100 text-red-700" },
  advisor: { label: "Advisor / Expert", cls: "bg-violet-100 text-violet-700" },
  advocate: { label: "Advocate / Adopter", cls: "bg-emerald-100 text-emerald-700" },
  observer: { label: "Observer / Monitor", cls: "bg-slate-200 text-slate-600" },
};

export default function RegistryPanel({ projectId, onAdded }) {
  const [registry, setRegistry] = useState(null);
  const [inProject, setInProject] = useState([]);
  const [adding, setAdding] = useState(null);
  const [editEntry, setEditEntry] = useState(null); // resource being edited, or "new"
  const [trendFor, setTrendFor] = useState(null);
  const [orgOpen, setOrgOpen] = useState(true);

  const load = () => Promise.all([
    api.get("/resource-registry"),
    projectId ? api.get(`/projects/${projectId}/resources`) : Promise.resolve({ data: [] }),
  ])
    .then(([r, p]) => { setRegistry(r.data); setInProject(p.data.map((x) => x.name.trim().toLowerCase())); })
    .catch(() => setRegistry([]));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addToProject = async (r) => {
    setAdding(r.name);
    try {
      await api.post(`/projects/${projectId}/resources`, {
        name: r.name, role: r.role || "", email: r.email || "",
        daily_rate: r.daily_rate || 0, capacity: r.capacity || 1,
      });
      toast.success(`"${r.name}" added to this project`);
      await load();
      onAdded?.();
    } catch (e) {
      toast.error("Could not add the resource");
    } finally { setAdding(null); }
  };

  if (!registry) return null;

  return (
    <div className="border border-border rounded-lg p-5" data-testid="resource-registry">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <BookMarked className="h-4 w-4 text-primary" />
          <p className="font-display font-bold">Resource registry</p>
          <span className="text-xs text-muted-foreground">{registry.length} saved across your projects & library</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setOrgOpen((v) => !v)} data-testid="registry-orgchart-button">
            <Network className="h-3.5 w-3.5" /> {orgOpen ? "Hide org chart" : "Org chart"}
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setEditEntry("new")} data-testid="registry-new-button">
            <Plus className="h-3.5 w-3.5" /> New registry entry
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Central contact + velocity record for everyone you work with. Emails here are used when sending meeting minutes. Velocity starts at Medium and recalibrates automatically as the system measures actual vs planned progress on their tasks.</p>
      {orgOpen && <EmbeddedOrgChart registry={registry} />}
      {registry.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">No resources saved yet — create your first registry entry.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {registry.map((r) => {
            const already = inProject.includes(r.name.trim().toLowerCase());
            return (
              <div key={r.name} className="border border-border rounded-md px-3 py-2.5 space-y-1.5" data-testid={`registry-item-${r.name}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{r.role || "No role set"}</p>
                  </div>
                  <button type="button" onClick={() => setTrendFor(r)} data-testid={`registry-trend-${r.name}`} title="View velocity trend">
                    <VelocityBadge velocity={r.velocity} />
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground space-y-0.5">
                  <p className="flex items-center gap-1.5 truncate"><Mail className="h-3 w-3 shrink-0" /> {r.email || <span className="text-muted-foreground/50">no email — add for minutes</span>}</p>
                  <p className="flex items-center gap-1.5 truncate"><Phone className="h-3 w-3 shrink-0" /> {r.phone || <span className="text-muted-foreground/50">no phone</span>}</p>
                  <p className="flex items-center gap-1.5 truncate"><Building2 className="h-3 w-3 shrink-0" /> {r.organization || <span className="text-muted-foreground/50">no organization</span>}</p>
                  <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                    {r.influence && INFLUENCE_META[r.influence] && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${INFLUENCE_META[r.influence].cls}`} data-testid={`registry-influence-${r.name}`}>{INFLUENCE_META[r.influence].label}</span>
                    )}
                    {r.reports_to && <span className="text-[10px] text-muted-foreground">↳ reports to {r.reports_to}</span>}
                  </div>
                  <p className="truncate opacity-70">{r.sources.join(", ")}</p>
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <button type="button" onClick={() => setEditEntry(r)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground" data-testid={`registry-edit-${r.name}`}>
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  {projectId && (already ? (
                    <span className="text-[11px] font-semibold text-emerald-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> In project</span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] gap-1" disabled={adding === r.name} onClick={() => addToProject(r)} data-testid={`registry-add-${r.name}`}>
                      {adding === r.name ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />} Add
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <RegistryEditDialog entry={editEntry} registry={registry} onOpenChange={(v) => !v && setEditEntry(null)} onSaved={() => { setEditEntry(null); load(); onAdded?.(); }} />
      <VelocityTrendDialog resource={trendFor} onOpenChange={(v) => !v && setTrendFor(null)} />
    </div>
  );
}

const renderOrgNode = (r, registry, depth) => {
  const kids = registry.filter((c) => (c.reports_to || "").trim().toLowerCase() === r.name.trim().toLowerCase());
  return (
    <div key={r.name} style={{ marginLeft: depth ? 24 : 0 }} className={depth ? "border-l-2 border-border pl-4 mt-2" : "mt-2"}>
      <div className="inline-flex items-center gap-2 border border-border rounded-md px-3 py-1.5 bg-secondary/20" data-testid={`orgchart-node-${r.name}`}>
        <div>
          <p className="text-sm font-semibold leading-tight">{r.name}</p>
          <p className="text-[11px] text-muted-foreground leading-tight">{[r.role, r.organization].filter(Boolean).join(" · ") || "—"}</p>
        </div>
        {r.influence && INFLUENCE_META[r.influence] && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${INFLUENCE_META[r.influence].cls}`}>{INFLUENCE_META[r.influence].label}</span>
        )}
      </div>
      {kids.map((c) => renderOrgNode(c, registry, depth + 1))}
    </div>
  );
};

function EmbeddedOrgChart({ registry }) {
  const list = registry || [];
  const byKey = Object.fromEntries(list.map((r) => [r.name.trim().toLowerCase(), r]));
  const roots = list.filter((r) => {
    const rt = (r.reports_to || "").trim().toLowerCase();
    return !rt || !byKey[rt];
  });
  const hasLinks = list.some((r) => (r.reports_to || "").trim());

  return (
    <div className="border border-border rounded-md bg-secondary/10 p-4 mb-4" data-testid="orgchart-embedded">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1">
        <Network className="h-3.5 w-3.5 text-primary" /> Org chart
      </p>
      {!hasLinks ? (
        <p className="text-sm text-muted-foreground" data-testid="orgchart-empty-hint">
          No reporting links yet — edit a resource and set "Reports to" to build the org chart.
        </p>
      ) : (
        <div className="-mt-1">{roots.map((r) => renderOrgNode(r, list, 0))}</div>
      )}
    </div>
  );
}

function VelocityTrendDialog({ resource, onOpenChange }) {
  const v = resource?.velocity || {};
  const history = (v.history || []).map((h, i) => ({ ...h, idx: i + 1 }));
  const s = VELOCITY_STYLE[v.level] || VELOCITY_STYLE.medium;
  return (
    <Dialog open={!!resource} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="velocity-trend-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" /> Velocity trend — {resource?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 text-sm">
          <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
          {v.samples
            ? <span className="text-muted-foreground text-xs">{v.measured}× planned pace · {v.samples} measurement{v.samples === 1 ? "" : "s"}</span>
            : <span className="text-muted-foreground text-xs">Default — no measurements yet</span>}
        </div>
        {history.length ? (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={history} margin={{ top: 10, right: 12, bottom: 0, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => (d || "").slice(5)} />
                <YAxis tick={{ fontSize: 9 }} domain={[0, "auto"]} allowDecimals />
                <Tooltip formatter={(val, name) => [`${val}×`, name === "ema" ? "Calibrated velocity" : "Task measurement"]} labelFormatter={(d) => `Measured ${d}`} />
                <ReferenceLine y={1} stroke="#94a3b8" strokeDasharray="4 3" label={{ value: "planned pace", fontSize: 9, fill: "#94a3b8", position: "right" }} />
                <Line type="monotone" dataKey="sample" stroke="#cbd5e1" strokeWidth={1} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="ema" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-[11px] text-muted-foreground">Blue = calibrated velocity (smoothed). Grey = individual task measurements. Above 1× means finishing faster than planned; below 1× means slower.</p>
          </>
        ) : (
          <div className="border border-dashed border-border rounded-md p-6 text-center text-sm text-muted-foreground" data-testid="velocity-trend-empty">
            No measurements yet. Velocity starts at <b>Medium</b> and recalibrates automatically as this person's tasks are trued-up across projects — the trend will appear here.
          </div>
        )}
        {(v.events || []).length > 0 && (
          <div data-testid="velocity-events">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Why Auto Pilot changed the rating</p>
            <div className="space-y-1.5">
              {[...v.events].reverse().map((ev, i) => {
                const up = RANK[ev.to] > RANK[ev.from];
                return (
                  <div key={i} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${up ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`} data-testid={`velocity-event-${i}`}>
                    {up ? <TrendingUp className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> : <TrendingDown className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />}
                    <span>
                      <b>{up ? "Promoted" : "Downgraded"} {VELOCITY_STYLE[ev.from]?.label} → {VELOCITY_STYLE[ev.to]?.label}</b>
                      <span className="text-muted-foreground"> · {ev.date}</span>
                      <span className="block text-muted-foreground mt-0.5">{ev.reason}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const VELOCITY_STYLE = {
  fast: { label: "Fast", cls: "bg-emerald-100 text-emerald-700" },
  medium: { label: "Medium", cls: "bg-blue-100 text-blue-700" },
  slow: { label: "Slow", cls: "bg-amber-100 text-amber-700" },
};
const RANK = { slow: 0, medium: 1, fast: 2 };

function VelocityBadge({ velocity }) {
  const v = velocity || { level: "medium", samples: 0 };
  const s = VELOCITY_STYLE[v.level] || VELOCITY_STYLE.medium;
  const last = (v.events || [])[v.events?.length - 1];
  const up = last ? RANK[last.to] > RANK[last.from] : null;
  const title = v.samples
    ? `Measured velocity ${v.measured}× planned pace over ${v.samples} sample${v.samples === 1 ? "" : "s"}${last ? ` · ${up ? "promoted" : "downgraded"} ${last.date}: ${last.reason}` : ""}`
    : "Default — recalibrates automatically as this person completes tasks";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${s.cls}`} title={title} data-testid="registry-velocity-badge">
      <Zap className="h-2.5 w-2.5" /> {s.label}{!v.samples && " *"}
      {last && (up
        ? <TrendingUp className="h-2.5 w-2.5 text-emerald-700" data-testid="velocity-promoted-arrow" />
        : <TrendingDown className="h-2.5 w-2.5 text-red-600" data-testid="velocity-downgraded-arrow" />)}
    </span>
  );
}

function RegistryEditDialog({ entry, registry, onOpenChange, onSaved }) {
  const isNew = entry === "new";
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [organization, setOrganization] = useState("");
  const [influence, setInfluence] = useState("none");
  const [reportsTo, setReportsTo] = useState("none");
  const [otherManager, setOtherManager] = useState("");
  const [saving, setSaving] = useState(false);
  const otherNames = (registry || []).map((r) => r.name).filter((n) => isNew || n !== entry?.name);

  useEffect(() => {
    if (entry && entry !== "new") {
      setName(entry.name); setRole(entry.role || ""); setEmail(entry.email || ""); setPhone(entry.phone || "");
      setOrganization(entry.organization || ""); setInfluence(entry.influence || "none");
      const rt = entry.reports_to || "";
      const known = (registry || []).some((r) => r.name === rt);
      if (rt && !known) { setReportsTo("__other__"); setOtherManager(rt); }
      else { setReportsTo(rt || "none"); setOtherManager(""); }
    } else { setName(""); setRole(""); setEmail(""); setPhone(""); setOrganization(""); setInfluence("none"); setReportsTo("none"); setOtherManager(""); }
  }, [entry, registry]);

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (reportsTo === "__other__" && !otherManager.trim()) return toast.error("Enter the manager's name");
    setSaving(true);
    try {
      await api.put("/resource-registry", {
        name: name.trim(), role: role.trim(), email: email.trim(), phone: phone.trim(),
        organization: organization.trim(),
        influence: influence === "none" ? "" : influence,
        reports_to: reportsTo === "none" ? "" : reportsTo === "__other__" ? otherManager.trim() : reportsTo,
      });
      toast.success(`Registry entry "${name.trim()}" saved`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save the registry entry");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="registry-edit-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "New registry entry" : `Edit ${entry?.name || ""}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isNew} placeholder="e.g. Dana Kim" data-testid="registry-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Role on the team</Label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="e.g. Developer" data-testid="registry-role-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Email (used for sending minutes)</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dana@company.com" data-testid="registry-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 0100" data-testid="registry-phone-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Organization</Label>
            <Input value={organization} onChange={(e) => setOrganization(e.target.value)} placeholder="e.g. Acme Corp — Product Group" data-testid="registry-org-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Level of influence</Label>
              <Select value={influence} onValueChange={setInfluence}>
                <SelectTrigger data-testid="registry-influence-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not set —</SelectItem>
                  <SelectItem value="decision_maker">Decision maker</SelectItem>
                  <SelectItem value="advisor">Advisor / Expert</SelectItem>
                  <SelectItem value="advocate">Advocate / Adopter</SelectItem>
                  <SelectItem value="observer">Observer / Monitor</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Feeds communication plan, escalation plan & stakeholder analysis.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Reports to (org chart)</Label>
              <Select value={reportsTo} onValueChange={setReportsTo}>
                <SelectTrigger data-testid="registry-reportsto-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No one / top level —</SelectItem>
                  {otherNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  <SelectItem value="__other__">Other — add a manager…</SelectItem>
                </SelectContent>
              </Select>
              {reportsTo === "__other__" && (
                <Input value={otherManager} onChange={(e) => setOtherManager(e.target.value)} placeholder="Manager's name" data-testid="registry-othermanager-input" />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="registry-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

